export type UserRole = 'admin' | 'it' | 'supervisor' | 'management' | 'finance' | 'user';

export type UserFilter = 'All' | 'Me' | string;

export interface CrmUser {
  id: string;
  username: string;
  display_name: string;
  role: UserRole;
  email?: string;
}

export interface AllowedEmail {
  id: string;
  email: string;
  display_name?: string | null;
  role: UserRole;
  username?: string | null;
  /** Application Form only — organizational department. */
  department_id?: string | null;
  /** Microsoft / Supabase auth user id after first sign-in. */
  auth_id?: string | null;
  created_at?: string;
}

export interface Client {
  id: string;
  company_name: string;
  code: string;
  client_type?: string;
  /**
   * For CRM clients this stores the NAV **salesperson code** (not a portal user id).
   * Contacts / projects / tasks still use owner_id as a person ref.
   */
  owner_id?: string;
  /** Parent account when this row is a brand / sub-company. */
  parent_client_id?: string | null;
  main_phone?: string;
  website?: string;
  address?: string;
  /** Singapore district / planning area (auto from address). */
  district?: string | null;
  description?: string;
  logo_path?: string;
  created_at?: string;
  /** Singapore Unique Entity Number (ACRA). */
  uen?: string | null;
  intel_html?: string | null;
  intel_updated_at?: string | null;
  /** Cached structured registry profile (ACRA). */
  intel_profile_json?: AccountIntelRegistry | null;
}

/** Structured company registry facts from ACRA (or null fields when unavailable). */
export interface AccountIntelRegistry {
  uen?: string | null;
  legalName?: string | null;
  status?: string | null;
  entityType?: string | null;
  registrationDate?: string | null;
  registeredAddress?: string | null;
  primarySsic?: string | null;
  paidUpCapital?: string | null;
  currency?: string | null;
  source?: string;
  fetchedAt?: string;
  error?: string | null;
}

export interface Contact {
  id: string;
  first_name: string;
  last_name: string;
  client_id: string;
  job_title?: string;
  email?: string;
  phone?: string;
  owner_id?: string;
  created_at?: string;
}

export interface Project {
  id: string;
  title: string;
  reference_no?: string;
  status?: string;
  client_id?: string;
  contact_id?: string | null;
  owner_id?: string;
  created_at?: string;
  /** Present when status is Completed (column name may be completed_date or completed_at in DB). */
  completed_date?: string | null;
  completed_at?: string | null;
}

export interface Task {
  id: string;
  title: string;
  status?: string;
  due_date?: string;
  client_id?: string | null;
  /** New customer name when no CRM account is linked yet. */
  prospect_company_name?: string | null;
  /** Linked meeting minutes record (if created from or attached to a minute). */
  meeting_minute_id?: string | null;
  /** Visit contact person (free text; not linked to contacts table). */
  contact_person?: string | null;
  contact_phone?: string | null;
  /** Visit address snapshot; defaults from account office address (falls back to ACRA registered), editable without changing clients. */
  address?: string | null;
  /** NAV outlet under mother account (e.g. S0646-S3). Null = mother / not specified. */
  outlet_code?: string | null;
  /** Outlet display name snapshot when outlet_code was set. */
  outlet_name?: string | null;
  /** Singapore postal used to resolve outlet district. */
  outlet_postal?: string | null;
  /** Fleet GPS stop key (startedAt|endedAt) when assigned from Fleet map. */
  fleet_stop_key?: string | null;
  description?: string;
  owner_id?: string;
  created_at?: string;
}

export interface TaskAttachment {
  id: string;
  task_id: string;
  file_name: string;
  content_type: string;
  byte_size: number;
  storage_path: string;
  uploaded_by?: string | null;
  created_at: string;
}

export interface ProjectUpdate {
  id: string;
  project_id: string;
  content?: string;
  author_id?: string;
  author_name?: string;
  created_at?: string;
  attachment_name?: string;
  attachment_url?: string;
}

export interface MeetingMinute {
  id: string;
  client_id: string;
  title: string;
  meeting_date?: string | null;
  /** @deprecated Use discussion — kept for legacy rows */
  body?: string;
  attendees?: string;
  discussion?: string;
  status?: 'draft' | 'posted';
  posted_at?: string | null;
  author_name?: string | null;
  owner_id?: string;
  created_at?: string;
  updated_at?: string;
}

export type TransferRequestStatus = 'pending' | 'approved' | 'rejected';

export interface AccountTransferRequest {
  id: string;
  client_id: string;
  from_owner_id: string;
  to_owner_id: string;
  requested_by: string;
  status: TransferRequestStatus;
  message?: string | null;
  review_note?: string | null;
  reviewed_by?: string | null;
  reviewed_at?: string | null;
  created_at?: string;
  include_sub_companies?: boolean;
  include_projects?: boolean;
  include_tasks?: boolean;
}

export interface CrmNotification {
  id: string;
  user_id: string;
  kind: string;
  title: string;
  body: string;
  link?: string | null;
  related_request_id?: string | null;
  read_at?: string | null;
  created_at?: string;
}

export interface AccountActivityLog {
  id: string;
  client_id: string;
  event_type: string;
  title: string;
  detail: string;
  actor_id?: string | null;
  related_request_id?: string | null;
  created_at?: string;
}

export interface CrmData {
  users: CrmUser[];
  clients: Client[];
  contacts: Contact[];
  projects: Project[];
  tasks: Task[];
  updates: ProjectUpdate[];
  meetingMinutes: MeetingMinute[];
  transferRequests: AccountTransferRequest[];
  notifications: CrmNotification[];
  activityLogs: AccountActivityLog[];
}

export const CLIENT_TYPES = [
  'Normal Customer',
  'Potential Customer',
  'VIP Customer',
  'Closed',
  'Bad Debt',
] as const;

/** IT Management account status values (stored in clients.client_type). */
export const IT_CLIENT_TYPES = ['Active', 'Inactive', 'Blocked', 'Potential'] as const;

export const PROJECT_STATUSES = ['Ongoing', 'On Hold', 'Completed'] as const;
export const TASK_STATUSES = ['To Do', 'In Progress', 'Done'] as const;
